import { useGetDeliveryZones, useGetDeliveryZonesDepot } from '@agos/api-client/orders'
import {
  T,
  courierQuoteOf,
  formatCents,
  formatDecimal,
  parseMoneyToCents,
  tt,
  usablePoint,
  type LatLng,
} from '@agos/shared'
import { Spinner, cn } from '@agos/ui'
import { MapPin, RotateCcw } from 'lucide-react'
import { Suspense, lazy, useMemo, useState } from 'react'

import { zoneFor, zonesOf, type DeliveryZone } from './delivery'
import { priceRows, shownZones, zoneOutline, type PriceRow } from './delivery-areas'
import { useDeliveryQuote } from './use-delivery-areas'

/*
 * FRONTEND.md §9.799 - Leaflet loads with this page and with nothing else: `@agos/ui/map` is imported lazily, as the
 * checkout's pin map is.
 */
const ZonesMap = lazy(() => import('@agos/ui/map').then((module) => ({ default: module.ZonesMap })))

/** A zone's colour, or the primary-ish blue the manager's first zone gets when a row carries none. */
const FALLBACK_COLOR = '#3b82f6'

/** `od 20,00 €`, `do 30,00 €`, `od 20,00 € do 30,00 €` - an order value range, as a person reads it. */
function rangeLabel(row: PriceRow): string {
  if (row.toCents === null) return row.fromCents === 0 ? tt('Každá objednávka') : `${tt('od')} ${formatCents(row.fromCents)}`
  if (row.fromCents === 0) return `${tt('do')} ${formatCents(row.toCents)}`
  return `${tt('od')} ${formatCents(row.fromCents)} ${tt('do')} ${formatCents(row.toCents)}`
}

function feeLabel(feeCents: number): string {
  return feeCents === 0 ? tt('zadarmo') : formatCents(feeCents)
}

/** What one zone costs - its minimum, then the fee by order value. */
function ZonePrices({ zone }: { zone: DeliveryZone }) {
  const minimumCents = parseMoneyToCents(zone.minOrder)
  return (
    <div className="space-y-2" data-delivery-area-prices={zone.id}>
      <p className="font-bold text-lg text-foreground flex items-center gap-2">
        <span className="w-3 h-3 rounded-full shrink-0" style={{ background: zone.color ?? FALLBACK_COLOR }} />
        {zone.name}
      </p>
      {minimumCents > 0 && (
        <p className="text-sm text-muted-foreground" data-delivery-area-minimum>
          <T>Minimálna objednávka</T>: <span className="font-semibold text-foreground">{formatCents(minimumCents)}</span>
        </p>
      )}
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs text-muted-foreground">
            <th className="font-semibold py-1">
              <T>Objednávka</T>
            </th>
            <th className="font-semibold py-1 text-right">
              <T>Doprava</T>
            </th>
          </tr>
        </thead>
        <tbody>
          {priceRows(zone).map((row) => (
            <tr key={row.fromCents} className="border-t border-border" data-delivery-area-row>
              <td className="py-1.5">{rangeLabel(row)}</td>
              <td className={cn('py-1.5 text-right font-semibold', row.feeCents === 0 && 'text-green-600')}>
                {feeLabel(row.feeCents)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

/**
 * `/oblasti-dorucenia` - where the restaurant delivers and what it costs, FRONTEND.md §9.799, owner O148.
 *
 * *"it will show these things like in správa rozvoz -> oblasti doručenia... with prices when you click on that
 * circle"*. A map and a list; choosing a zone in either shows its prices. A click on the map is answered by `zoneFor`
 * - the checkout's own rule, tie-break included - so the page names the zone an order to that spot is priced by.
 */
export function DeliveryAreasPage() {
  const zonesRead = useGetDeliveryZones()
  const { data: depotData } = useGetDeliveryZonesDepot({ query: { staleTime: 30 * 60_000 } })
  const quote = useDeliveryQuote()
  const zones = useMemo(() => shownZones(zonesOf(zonesRead.data)), [zonesRead.data])
  const depot = useMemo(() => usablePoint(depotData), [depotData])
  const outlines = useMemo(
    () => zones.map((zone) => ({ id: zone.id, color: zone.color ?? FALLBACK_COLOR, outline: zoneOutline(zone) })),
    [zones],
  )
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [outside, setOutside] = useState(false)
  const selected = zones.find((zone) => zone.id === selectedId) ?? null
  const courier = courierQuoteOf(quote.data)

  const pick = (point: LatLng) => {
    const zone = zoneFor(point.lat, point.lng, zones, { depot })
    setSelectedId(zone?.id ?? null)
    setOutside(zone === null)
  }

  const choose = (id: string) => {
    setSelectedId(id)
    setOutside(false)
  }

  return (
    <div className="max-w-5xl mx-auto px-4 py-6 space-y-5" data-delivery-areas>
      <div>
        <h1 className="text-2xl font-black text-foreground flex items-center gap-2">
          <MapPin className="w-6 h-6 text-primary" />
          <T>Oblasti doručenia</T>
        </h1>
        {courier?.external !== true && zones.length > 0 && (
          <p className="text-sm text-muted-foreground mt-1">
            <T>Kliknite na oblasť na mape alebo v zozname a uvidíte, koľko stojí doručenie.</T>
          </p>
        )}
      </div>

      {zonesRead.isLoading ? (
        <div className="flex justify-center py-16">
          <Spinner />
        </div>
      ) : courier?.external === true ? (
        // §9.786 - where external couriers deliver, no zone prices anything: the fee is the distance.
        <p className="rounded-2xl border border-border bg-card p-5 text-sm" data-delivery-areas-distance>
          <T>Doručenie počítame podľa vzdialenosti</T>
          {courier.maxKm !== null && (
            <>
              {', '}
              <T>najďalej</T> {formatDecimal(courier.maxKm, 1)} km
            </>
          )}
          . <T>Presnú cenu uvidíte v košíku po zadaní adresy.</T>
        </p>
      ) : zonesRead.isError ? (
        <div className="rounded-2xl border border-border bg-card p-5 space-y-3" data-delivery-areas-failed role="alert">
          <p className="text-sm">
            <T>Oblasti doručenia sa nepodarilo načítať.</T>
          </p>
          <button
            type="button"
            onClick={() => void zonesRead.refetch()}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-primary text-primary-foreground text-sm font-bold"
            data-delivery-areas-retry
          >
            <RotateCcw className="w-4 h-4" /> <T>Skúsiť znova</T>
          </button>
        </div>
      ) : zones.length === 0 ? (
        <p className="rounded-2xl border border-border bg-card p-5 text-sm" data-delivery-areas-none>
          <T>Oblasti doručenia zatiaľ nie sú nastavené.</T>
        </p>
      ) : (
        <>
          <div className="grid gap-4 md:grid-cols-[1fr_320px]">
            <div className="h-[360px] md:h-[440px] rounded-2xl overflow-hidden border border-border" data-zones-map>
              <Suspense
                fallback={
                  <div className="h-full flex items-center justify-center">
                    <Spinner />
                  </div>
                }
              >
                <ZonesMap zones={outlines} selectedId={selectedId} onPick={pick} />
              </Suspense>
            </div>
            <div className="rounded-2xl border border-border bg-card p-4" data-delivery-area-detail>
              {selected !== null ? (
                <ZonePrices zone={selected} />
              ) : outside ? (
                <p className="text-sm" data-delivery-area-outside>
                  <T>Sem zatiaľ nedoručujeme.</T>
                </p>
              ) : (
                <p className="text-sm text-muted-foreground">
                  <T>Vyberte oblasť na mape alebo v zozname.</T>
                </p>
              )}
            </div>
          </div>

          <ul className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3" data-delivery-area-list>
            {zones.map((zone) => {
              const minimumCents = parseMoneyToCents(zone.minOrder)
              return (
                <li key={zone.id}>
                  <button
                    type="button"
                    onClick={() => choose(zone.id)}
                    aria-pressed={zone.id === selectedId}
                    data-delivery-area={zone.id}
                    className={cn(
                      'w-full flex items-center gap-3 rounded-xl border px-3 py-2.5 text-left transition-colors',
                      zone.id === selectedId ? 'border-primary bg-primary/5' : 'border-border hover:bg-secondary',
                    )}
                  >
                    <span className="w-3 h-3 rounded-full shrink-0" style={{ background: zone.color ?? FALLBACK_COLOR }} />
                    <span className="flex-1 min-w-0">
                      <span className="block font-semibold text-sm truncate">{zone.name}</span>
                      {minimumCents > 0 && (
                        <span className="block text-xs text-muted-foreground">
                          <T>Minimálna objednávka</T> {formatCents(minimumCents)}
                        </span>
                      )}
                    </span>
                  </button>
                </li>
              )
            })}
          </ul>
        </>
      )}
    </div>
  )
}
